import React, { useState, useEffect, useCallback } from 'react';
import { 
  Button, 
  Space, 
  Tag, 
  Typography, 
  message, 
  Modal, 
  Form, 
  InputNumber, 
  Switch, 
  Input,
  Select,
  Popconfirm,
  Spin,
  Table,
  Tooltip,
  Badge,
} from 'antd';
import { 
  ReloadOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  bgTertiary: '#21262D',
  border: '#30363D',
  textPrimary: '#E6EDF3',
  textSecondary: '#8B949E',
  textMuted: '#6E7681',
  accent: '#58A6FF',
  accentGreen: '#3FB950',
  accentRed: '#F85149',
  accentOrange: '#F0883E',
};

const { Title } = Typography;
const { TextArea } = Input;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  min-height: 100vh;
  
  /* Ensure all text is visible */
  * {
    color: inherit;
  }
  
  /* Fix Badge text */
  .ant-badge-status-text {
    color: ${COLORS.textPrimary} !important;
  }
  
  /* Fix Tag text */
  .ant-tag {
    color: ${COLORS.textPrimary} !important;
  }
  
  /* Fix Tooltip text */
  .ant-tooltip-inner {
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgSecondary} !important;
  }
  
  /* Fix Popconfirm text */
  .ant-popover-inner-content {
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 28px !important;
    font-weight: 700 !important;
    background: linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentGreen} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const StyledCard = styled.div`
  background: ${COLORS.bgSecondary};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  
  .ant-table {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr > td {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
    
    span {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: ${COLORS.bgTertiary} !important;
  }
  
  /* Force all table text to be visible */
  .ant-table-cell span,
  .ant-table-cell {
    color: ${COLORS.textPrimary} !important;
  }
  
  /* Force tag text to be visible */
  .ant-tag {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-tag.ant-tag-blue {
    color: ${COLORS.textPrimary} !important;
    background-color: rgba(24, 144, 255, 0.2) !important;
  }
  
  .ant-tag.ant-tag-green {
    color: ${COLORS.textPrimary} !important;
    background-color: rgba(52, 211, 153, 0.2) !important;
  }
  
  .ant-tag.ant-tag-red {
    color: ${COLORS.textPrimary} !important;
    background-color: rgba(248, 81, 73, 0.2) !important;
  }
  
  .ant-tag.ant-tag-orange {
    color: ${COLORS.textPrimary} !important;
    background-color: rgba(240, 136, 62, 0.2) !important;
  }
  
  /* Force Badge text to be visible */
  .ant-badge-status-text {
    color: ${COLORS.textPrimary} !important;
  }
  
  /* Pagination styling */
  .ant-pagination {
    color: ${COLORS.textPrimary} !important;
    margin-top: 16px;
    
    .ant-pagination-item {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      
      a {
        color: ${COLORS.textPrimary} !important;
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.accent} !important;
        border-color: ${COLORS.accent} !important;
        
        a {
          color: white !important;
        }
      }
      
      &:hover {
        border-color: ${COLORS.accent} !important;
        
        a {
          color: ${COLORS.accent} !important;
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        background: ${COLORS.bgPrimary} !important;
        border-color: ${COLORS.border} !important;
        color: ${COLORS.textPrimary} !important;
        
        &:hover {
          border-color: ${COLORS.accent} !important;
          color: ${COLORS.accent} !important;
        }
      }
    }
    
    .ant-pagination-options {
      .ant-select {
        .ant-select-selector {
          background: ${COLORS.bgPrimary} !important;
          border-color: ${COLORS.border} !important;
          color: ${COLORS.textPrimary} !important;
        }
        
        .ant-select-selection-item {
          color: ${COLORS.textPrimary} !important;
        }
      }
    }
    
    .ant-pagination-total-text {
      color: ${COLORS.textPrimary} !important;
      font-size: 14px !important;
    }
    
    .ant-pagination-options-quick-jumper {
      color: ${COLORS.textPrimary} !important;
      
      input {
        background: ${COLORS.bgPrimary} !important;
        border-color: ${COLORS.border} !important;
        color: ${COLORS.textPrimary} !important;
      }
    }
  }
  
  /* Select dropdown styling */
  .ant-select-dropdown {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
    
    .ant-select-item {
      background: ${COLORS.bgSecondary} !important;
      color: ${COLORS.textPrimary} !important;
      
      &:hover {
        background: ${COLORS.bgTertiary} !important;
      }
      
      &.ant-select-item-option-selected {
        background: ${COLORS.accent} !important;
        color: white !important;
      }
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-modal-header {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-body {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-modal-footer {
    background: ${COLORS.bgSecondary} !important;
    border-top: 1px solid ${COLORS.border} !important;
  }
  
  .ant-form-item-label > label {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-input-number-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
`;

interface VatConfiguration {
  id: string;
  countryCode: string | null;
  countryName: string | null;
  vatType: 'PERCENTAGE' | 'FIXED';
  vatRate: number;
  isDefault: boolean;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

const VatConfigurations: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configurations, setConfigurations] = useState<VatConfiguration[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<VatConfiguration | null>(null);
  const [form] = Form.useForm();

  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_VAT_CONFIGURATIONS);
      
      if (response.data?.status === 'success' && response.data?.data) {
        setConfigurations(response.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch VAT configurations');
      console.error('Error fetching VAT configurations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const handleCreate = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({
      vatType: 'PERCENTAGE',
      vatRate: 0,
      isDefault: false,
      isActive: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (config: VatConfiguration) => {
    setEditingConfig(config);
    form.setFieldsValue({
      countryCode: config.countryCode || undefined,
      countryName: config.countryName || undefined,
      vatType: config.vatType,
      vatRate: parseFloat(config.vatRate.toString()),
      isDefault: config.isDefault,
      isActive: config.isActive,
      description: config.description || undefined,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingConfig) {
        // Update existing
        const endpoint = API_ENDPOINTS.ADMIN_UPDATE_VAT_CONFIGURATION.replace('{id}', editingConfig.id);
        await apiClient.put(endpoint, values);
        message.success('VAT configuration updated successfully');
      } else {
        // Create new
        await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_VAT_CONFIGURATION, values);
        message.success('VAT configuration created successfully');
      }

      setModalVisible(false);
      fetchConfigurations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save VAT configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const endpoint = API_ENDPOINTS.ADMIN_DELETE_VAT_CONFIGURATION.replace('{id}', id);
      await apiClient.delete(endpoint);
      message.success('VAT configuration deleted successfully');
      fetchConfigurations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete VAT configuration');
    }
  };

  const columns = [
    {
      title: 'Country',
      dataIndex: 'countryName',
      key: 'countryName',
      render: (text: string | null, record: VatConfiguration) => {
        if (record.isDefault) {
          return <Tag color="blue" style={{ color: COLORS.textPrimary }}>Default</Tag>;
        }
        return <span style={{ color: COLORS.textPrimary }}>{text || record.countryCode || '-'}</span>;
      },
    },
    {
      title: 'Country Code',
      dataIndex: 'countryCode',
      key: 'countryCode',
      render: (text: string | null) => (
        <span style={{ color: COLORS.textPrimary }}>{text || '-'}</span>
      ),
    },
    {
      title: 'VAT Type',
      dataIndex: 'vatType',
      key: 'vatType',
      render: (type: string) => (
        <Tag color={type === 'PERCENTAGE' ? 'green' : 'orange'} style={{ color: COLORS.textPrimary }}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'VAT Rate',
      dataIndex: 'vatRate',
      key: 'vatRate',
      render: (rate: number, record: VatConfiguration) => {
        const display = record.vatType === 'PERCENTAGE' ? `${rate}%` : `$${rate.toFixed(2)}`;
        return <span style={{ color: COLORS.textPrimary }}>{display}</span>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: VatConfiguration) => (
        <Space>
          {record.isDefault && (
            <Badge 
              status="processing" 
              text={<span style={{ color: COLORS.textPrimary, fontSize: '14px' }}>Default</span>} 
            />
          )}
          {record.isActive ? (
            <Tag color="green" icon={<CheckCircleOutlined />} style={{ color: COLORS.textPrimary }}>
              Active
            </Tag>
          ) : (
            <Tag color="red" icon={<CloseCircleOutlined />} style={{ color: COLORS.textPrimary }}>
              Inactive
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: VatConfiguration) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: COLORS.accent }}
          >
            Edit
          </Button>
          {!record.isDefault && (
            <Popconfirm
              title="Are you sure you want to delete this VAT configuration?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                style={{ color: COLORS.accentRed }}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, margin: 0 }}>
          VAT Configurations
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchConfigurations}
            loading={loading}
            style={{ 
              color: COLORS.textPrimary, 
              borderColor: COLORS.border,
              background: COLORS.bgTertiary,
            }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{ background: COLORS.accentGreen, borderColor: COLORS.accentGreen }}
          >
            Create VAT Configuration
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={configurations}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => (
                <span style={{ color: COLORS.textPrimary, fontSize: '14px' }}>
                  Total {total} configurations
                </span>
              ),
            }}
          />
        </Spin>
      </StyledCard>

      <StyledModal
        title={editingConfig ? 'Edit VAT Configuration' : 'Create VAT Configuration'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSave}
        confirmLoading={saving}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            vatType: 'PERCENTAGE',
            vatRate: 0,
            isDefault: false,
            isActive: true,
          }}
        >
          <Form.Item
            name="countryCode"
            label={
              <Space>
                <span>Country Code</span>
                <Tooltip title="ISO 3166-1 alpha-2 country code (e.g., US, GB, NG). Leave empty for default VAT.">
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary }} />
                </Tooltip>
              </Space>
            }
            rules={[
              { len: 2, message: 'Country code must be 2 characters' },
            ]}
          >
            <Input
              placeholder="e.g., US, GB, NG (leave empty for default)"
              maxLength={2}
              style={{ textTransform: 'uppercase' }}
              onInput={(e: any) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
          </Form.Item>

          <Form.Item
            name="countryName"
            label="Country Name"
          >
            <Input placeholder="e.g., United States, United Kingdom, Nigeria" />
          </Form.Item>

          <Form.Item
            name="vatType"
            label="VAT Type"
            rules={[{ required: true, message: 'Please select VAT type' }]}
          >
            <Select
              dropdownStyle={{
                background: COLORS.bgSecondary,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <Select.Option 
                value="PERCENTAGE"
                style={{
                  background: COLORS.bgSecondary,
                  color: COLORS.textPrimary,
                }}
              >
                Percentage (e.g., 20%)
              </Select.Option>
              <Select.Option 
                value="FIXED"
                style={{
                  background: COLORS.bgSecondary,
                  color: COLORS.textPrimary,
                }}
              >
                Fixed Amount (e.g., $5.00)
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="vatRate"
            label="VAT Rate"
            rules={[
              { required: true, message: 'Please enter VAT rate' },
              { type: 'number', min: 0, message: 'VAT rate must be 0 or greater' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={4}
              placeholder={form.getFieldValue('vatType') === 'PERCENTAGE' ? 'e.g., 20.00 for 20%' : 'e.g., 5.00 for $5.00'}
            />
          </Form.Item>

          <Form.Item
            name="isDefault"
            valuePropName="checked"
            label={
              <Space>
                <span>Default VAT</span>
                <Tooltip title="If checked, this will become the default VAT configuration used for countries without specific VAT settings. Only one default can exist.">
                  <InfoCircleOutlined style={{ color: COLORS.textSecondary }} />
                </Tooltip>
              </Space>
            }
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isActive"
            valuePropName="checked"
            label="Active"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Optional description or notes about this VAT configuration"
            />
          </Form.Item>
        </Form>
      </StyledModal>
    </PageContainer>
  );
};

export default VatConfigurations;
